<template>
  <div
    :class="stickyClasses"
    :style="stickyStyles"
    >
    <div
      class="
        @600/layout/flex:column
        @600/layout/height
        $color/background:gray:F6F6F6
        color/background
        layout/width:100%
      "
      style="
        --600--layout--height: calc(100vh - var(--header-height--current));
      "
      >
      <div
        class="
          @480/layout/height
          @600/layout/flex:grow
          $color/background:alpha:54%
          $color/background:gray:eee
          color/background
          layout/align:center
          layout/flex
          layout/height
          layout/justify:center
          layout/overflow:hidden
          layout/width:100%
        "
        style="
          --480--layout--height: 416px;
          --layout--height: 286px;
        "
        >
        <div
          id="map"
          class="
            layout/height:100%
            layout/overflow:hidden
            layout/width:100%
          "
          />
      </div>

      <toggleSection
        :current-floor="currentFloor"
        :handle-floor-selection="setCurrentFloor"
      />
    </div>
  </div>
</template>

<script>
  import L from 'leaflet';
  import toggleSection from './floor-toggle-section.vue';
  import StringTemplates from './string-templates';
  import { ArtRoomsByFloor } from 'Helpers/dvg-helpers/map-page-helpers';

  const minZoom = 0.5;

  export default {
    components: {
      toggleSection,
    },
    props: {
      currentFloor: {
        type: String,
        required: true
      },
      setCurrentFloor: {
        type: Function,
        required: true
      },
      highlightedElementId: {
        type: String,
        required: false,
        default: null
      },
      activeArtRoomsByHlIds: {
        type: Array,
        required: true
      },
      activeArtRoomsReady: {
        type: Boolean,
        required: true
      }
    },
    data() {
      return {
        map: null,
        svgOverlay: null,
        highlighedElementBaseColor: null,
        isAnimating: false,
        mapContainer: null,
        allowAutoZoomOut: true,
      };
    },
    computed: {
      floorMapUrl() {
        return `../../../../dist/sprites/map_floors/map--floor-${this.currentFloor}.svg`;
      },
      stickyClasses() {
        return [
          '@600/layout/grid:column:span:2',
          '@600/layout/height',
          '@600/layout/order:2',
          '$motion/transition:top:curve:linear',
          '$motion/transition:top:duration:100ms',
          'layout/grid:column:span:1',
          'layout/height:100%',
          'layout/order:1',
          'layout/sticky',
          'layout/top',
          'layout/width:100%',
          'layout/z-index:1',
          'motion/transition'
        ];
      },
      stickyStyles() {
        return {
          '--600--layout--height': 'calc(100vh - var(--header-height--current))',
          '--layout--top': 'var(--header-height--current)'
        };
      },
    },
    watch: {
      activeArtRoomsReady(ready) {
        if (ready) this.setInactiveRooms();
      },
      currentFloor: function () {
        this.map?.remove();
        this.map = null;
        this.mainSetup();
      },
      highlightedElementId(newElementId, oldElementId) {
        if (oldElementId) {
          const oldHighlight = document.getElementById(oldElementId);
          oldHighlight?.setAttribute("fill", this.highlighedElementBaseColor);
        }
        if (newElementId != null) {
          const newHighlight = document.getElementById(newElementId);
          this.highlighedElementBaseColor = newHighlight.getAttribute("fill");
          newHighlight?.setAttribute("fill", "#fac300");
          this.centerMapOnSvgElement(newElementId);
        } else if (this.allowAutoZoomOut) { // only happens when the drawer is closing
          this.map?.flyTo([0, 0], minZoom, {
            duration: 0.75
          });
        }
      },
    },
    mounted() {
      this.mapContainer = document.getElementById("map");
      this.mainSetup();
      this.watchForScreenResize();
    },
    methods: {
      /**
       * Leaflet configuration function
       * Fetches the SVG file for the current floor then passes it to the leaflet setup function.
       * It then overrides leaflet zoom control styles to match designs.
       */
      async mainSetup() {
        try {
          const response = await fetch(this.floorMapUrl);
          const svgText = await response.text();
          const parser = new DOMParser();
          const svg = parser.parseFromString(svgText, "image/svg+xml").querySelector("svg");
          svg.setAttribute("width", "100%");
          svg.setAttribute("height", "100%");
          svg.setAttribute("viewBox", "0 0 783 286");
          svg.setAttribute("id", "svgOverlay");
          this.setup(svg);
          this.overrideZoomControlStyles();
        } catch (error) {
          if (window?.MoMA?.appInfo?.environment !== 'production') {
            console.error('Error fetching SVG file:', error);
          }
        }
      },
      /**
       * Leaflet configuration function
       * This function sets up the leaflet map with the SVG overlay.
       * @param {*} svg the SVG element to be used as the map overlay
       */
      setup(svg) {
        this.map = L.map("map", {
          crs: L.CRS.Simple,
          minZoom: minZoom,
          maxZoom: 3,
          maxBoundsViscosity: 1,
          zoomControl: false,
          attributionControl: false,
          zoomSnap: 0.5
        });

        L.control.zoom({ position: "bottomright" }).addTo(this.map);

        const { bounds } = this.getMapBounds();

        this.svgOverlay = L.svgOverlay(svg, bounds).addTo(this.map);

        this.map.setView([0, 0], minZoom);
        this.map.setMaxBounds(this.svgOverlay.getBounds());

        // Ensure pointer events are enabled for the SVG overlay
        const svgElement = document.getElementById("svgOverlay");
        if (svgElement) svgElement.style.pointerEvents = 'all';

         // Add event listeners to SVG elements immediately after SVG is added
         const highlightsGroup = svg.querySelector("#Highlights");
        if (highlightsGroup) {
          const elements = highlightsGroup.querySelectorAll("path, rect");
          elements.forEach(element => {
            element.style.pointerEvents = 'all';
            element.addEventListener('click', this.onElementClick);
          });
        }
        if (this.activeArtRoomsReady) this.setInactiveRooms();
      },
      /**
       * Leaflet configuration function
       * Overrides the default leaflet zoom control styles to match designs.
       */
      overrideZoomControlStyles() {
        const zoomControlContainer = document.querySelector(".leaflet-control");
        zoomControlContainer.classList = "$typography/size:large layout/grid $color/background:alpha:100%";
        const zoomInControl = document.querySelector(".leaflet-control-zoom-in");
        const zoomOutControl = document.querySelector(".leaflet-control-zoom-out");
        const zoomButtonClasses = `
          @768/layout/height:cap:3
          @768/layout/width:cap:3
          layout/align:center
          layout/height:cap:2.5
          layout/justify:center
          layout/width:cap:2.5
          link/focus`;
        zoomInControl.classList = zoomButtonClasses;
        zoomOutControl.classList = zoomButtonClasses;
        zoomOutControl.classList.add("leaflet-disabled");
        zoomInControl.innerHTML = StringTemplates.zoomIn;
        zoomOutControl.innerHTML = StringTemplates.zoomOut;
      },
      watchForScreenResize() {
        const resizeObserver = new ResizeObserver(() => {
          this.handleResize();
        });
        resizeObserver.observe(this.mapContainer);
      },
      onElementClick(event) {
        const target = event.target;
        const elementId = target.id; // or any other attribute you want to use for identification
        this.allowAutoZoomOut = false;
        this.$emit("elementClicked", elementId);
      },
      centerMapOnSvgElement(elementId) {
        const svg = this.svgOverlay._image; // Get the SVG element
        const svgElement = svg.getElementById(elementId); // Get the specific SVG element by ID

        if (svgElement) {
          this.isAnimating = true;
          const bbox = svgElement.getBBox(); // Get bounding box of the SVG element
          
          // Calculate the center of the bounding box
          const centerX = bbox.x + bbox.width / 2;
          const centerY = bbox.y + bbox.height / 2;
          
          // Convert SVG coordinates to map coordinates
          const svgWidth = svg.viewBox.baseVal.width;
          const svgHeight = svg.viewBox.baseVal.height;

          // Get the bounds of the SVG overlay
          const latLngBounds = this.svgOverlay.getBounds();
          const latDiff = latLngBounds.getNorth() - latLngBounds.getSouth();
          const lngDiff = latLngBounds.getEast() - latLngBounds.getWest();

          const lat = latLngBounds.getSouth() + (centerY / svgHeight) * latDiff;
          const lng = latLngBounds.getWest() + (centerX / svgWidth) * lngDiff;

          const mapContainerWidth = this.mapContainer.clientWidth;
          const breakpointForLessZoom = 540;
          const zoomLevel = mapContainerWidth >= breakpointForLessZoom ? 2 : 2.5;

          // Center the map on the calculated coordinates
          this.map?.flyTo([-lat, lng], zoomLevel, {
            duration: 0.75
          });
          this.map?.once("moveend", () => {
            this.isAnimating = false;
          });
        }
      },
      getMapBounds() {
        const mapContainerWidth = this.mapContainer.clientWidth;
        const mapContainerHeight = mapContainerWidth / (783 / 286);

        return {
          bounds: [
            [-mapContainerHeight / 2, -mapContainerWidth / 2],
            [mapContainerHeight / 2, mapContainerWidth / 2]
          ]
        };
      },
      handleResize() {
        this.$nextTick(() => {
          if (this.map && this.isAnimating === false) {
            this.map.invalidateSize();

            const { bounds } = this.getMapBounds();

            if (this.svgOverlay) {
              this.svgOverlay.setBounds(bounds); // Update the SVG overlay bounds
            }
          }
        });
      },
      setInactiveRooms() {
        ArtRoomsByFloor[this.currentFloor].forEach(highlightId => {
          if (!this.activeArtRoomsByHlIds.includes(highlightId)) {
            const element = document.getElementById(highlightId);
            if (element) element.style.fill = "#DDDDDD";
          }
        });
      }
    }
  };
</script>

<style>
  *,
  html,
  body {
    margin: 0;
  }
  #map {
    background-color: rgba(238, 238, 238, 0)
  }
  #zoom-out {
    border-top-color: black;
  }

  /* override leaflet default styling for zoom controls */
  .leaflet-right {
    right: var(--page-spacing--fixed);
  }
  .leaflet-bottom {
    bottom: var(--page-spacing--fixed);
  }
  .leaflet-disabled {
    cursor: default;
  }
  a.leaflet-disabled span {
    border-color: rgba(0, 0, 0, 0.40);
  }
  a.leaflet-disabled path {
    stroke: rgba(0, 0, 0, 0.40);
  }
</style>